// Generated with util/create-component.js
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Tag, Dropdown, MenuProps } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { DownOutlined, PercentageOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { FULL_DATE_TIME_REVERSED_FORMAT } from 'constants/date_formats';
import { IPromoCodesProps, IPromoCodeItem } from './promo_codes.types';
import styles from './promo_codes.module.scss';

const PromoCodes: React.FC<IPromoCodesProps> = ({ promoCodes, loading, pagination, handleEdit, handlePageChange }) => {
  const { t } = useTranslation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const tableScroll = useMemo(() => {
    if (1200 < windowWidth) return { y: window.innerHeight - 310, x: 'max-content' };
    if (770 < windowWidth) return { y: window.innerHeight - 450, x: 'max-content' };
    return { y: window.innerHeight - 510, x: 'max-content' };
  }, [windowWidth]);

  const tableColumns: ColumnsType<IPromoCodeItem> = [
    {
      title: t('general.name'),
      dataIndex: 'name',
    },
    {
      title: t('general.code'),
      dataIndex: 'code',
    },
    {
      title: t('general.amount'),
      dataIndex: 'amount',
      width: 100,
    },
    {
      title: t('general.type'),
      dataIndex: 'codeType',
      width: 100,
      render: (codeType: string) => (codeType === 'percentage' ? <PercentageOutlined /> : <DollarOutlined />),
    },
    {
      title: t('promo_codes.validation'),
      render: (record: IPromoCodeItem) => {
        const { validFrom, validTo } = record;
        if (!validFrom || !validTo) return '';
        return `${dayjs(validFrom).format(FULL_DATE_TIME_REVERSED_FORMAT)} - ${dayjs(validTo).format(
          FULL_DATE_TIME_REVERSED_FORMAT,
        )}`;
      },
    },
    {
      title: t('general.status'),
      render: (record: IPromoCodeItem) => {
        const { status, validFrom, validTo } = record;
        const isActive = status === 'active';
        let color = 'red';
        let text = t('contracts.inactive');

        if (isActive) {
          color = 'green';
          text = t('contracts.active');

          if (validFrom && dayjs(validFrom).isAfter(dayjs())) {
            color = 'blue';
            text = t('promo_codes.upcoming');
          } else if (validTo && dayjs(validTo).isBefore(dayjs())) {
            color = 'orange';
            text = t('promo_codes.expired');
          }
        }

        return (
          <Tag color={color} className={styles.tag}>
            {text}
          </Tag>
        );
      },
    },
    {
      key: 'actions',
      render: (_, record) => (
        <Dropdown menu={renderMenu(record)}>
          <div className={styles.actions_link}>
            <span className={styles.action_link_text}>{t('general.actions')}</span> <DownOutlined />
          </div>
        </Dropdown>
      ),
    },
  ];

  const renderMenu = (promoCode: IPromoCodeItem): MenuProps => {
    const menuItems: MenuProps['items'] = [
      {
        label: t('general.buttons.edit'),
        key: 'edit',
        onClick: () => handleEdit(promoCode),
      },
    ];

    return { items: menuItems };
  };

  return (
    <div data-testid="PromoCodes" className={styles.root}>
      <Table
        loading={loading}
        dataSource={promoCodes}
        columns={tableColumns}
        rowKey="id"
        tableLayout="fixed"
        data-testid="PromoCodesTable"
        scroll={tableScroll}
        pagination={pagination}
        onChange={handlePageChange}
      />
    </div>
  );
};

export default PromoCodes;
